import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { LoadingSpinner, CollapsibleSection } from '../../../components/layout/SharedComponents';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

export function ContractReviewSettingsPanel() {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">Contracts</h1>
      <p className="text-lg font-medium text-muted-foreground mb-6">Contract Review Settings</p>
      <ReviewStagesConfig />
      <div className="mt-6" />
      <DefaultStageGroupsConfig />
    </div>
  );
}

function ReviewStagesConfig() {
  const [stages, setStages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newStage, setNewStage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/contracts/admin-settings/stage-groups').then(r => {
      setStages(r.data.review_stages || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const MAX_STAGES = 5;

  const addStage = () => {
    if (!newStage.trim()) return;
    if (stages.length >= MAX_STAGES) { toast.error(`Maximum ${MAX_STAGES} review stages allowed`); return; }
    const id = newStage.trim().toUpperCase().replace(/\s+/g, '_');
    if (stages.some(s => s.id === id)) { toast.error('Stage already exists'); return; }
    setStages(prev => [...prev, { id, label: newStage.trim() }]);
    setNewStage('');
  };

  const removeStage = (idx) => setStages(prev => prev.filter((_, i) => i !== idx));
  const renameStage = (idx, label) => setStages(prev => prev.map((s, i) => i === idx ? { ...s, label } : s));
  const moveStage = (idx, dir) => {
    setStages(prev => {
      const arr = [...prev];
      const t = idx + dir;
      if (t < 0 || t >= arr.length) return arr;
      [arr[idx], arr[t]] = [arr[t], arr[idx]];
      return arr;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/contracts/admin-settings/review-stages', { review_stages: stages });
      toast.success('Review stages saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <CollapsibleSection title="Review Stages" defaultOpen>
      <p className="text-sm text-muted-foreground mb-4">
        Define your default stages for contract approvals. Stages appear in the order below. 'Draft' and 'Executed' are always present.
      </p>
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border">
          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold">1</div>
          <span className="text-sm font-medium text-muted-foreground">Draft</span>
          <span className="text-xs text-muted-foreground ml-auto">(always present)</span>
        </div>
        {stages.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg border bg-card">
            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">{i + 2}</div>
            <Input value={s.label} onChange={e => renameStage(i, e.target.value)} className="h-7 text-sm flex-1" />
            <div className="flex gap-0.5">
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveStage(i, -1)} disabled={i === 0}><ChevronUp className="h-3 w-3" /></Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveStage(i, 1)} disabled={i === stages.length - 1}><ChevronDown className="h-3 w-3" /></Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => removeStage(i)}><Trash2 className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border">
          <div className="w-6 h-6 rounded-full bg-green-200 flex items-center justify-center text-[10px] font-bold text-green-700">{stages.length + 2}</div>
          <span className="text-sm font-medium text-muted-foreground">Executed</span>
          <span className="text-xs text-muted-foreground ml-auto">(always present)</span>
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        <Input value={newStage} onChange={e => setNewStage(e.target.value)} placeholder="New stage name..." className="h-8 text-sm" onKeyDown={e => e.key === 'Enter' && addStage()} disabled={stages.length >= MAX_STAGES} />
        <Button size="sm" onClick={addStage} disabled={!newStage.trim() || stages.length >= MAX_STAGES}><Plus className="h-3 w-3 mr-1" /> Add Stage</Button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">{stages.length} of {MAX_STAGES} review stages used.</p>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Stages'}</Button>
      </div>
    </CollapsibleSection>
  );
}

function DefaultStageGroupsConfig() {
  const [stageGroups, setStageGroups] = useState({});
  const [stages, setStages] = useState([]);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/contracts/admin-settings/stage-groups').then(r => {
      setStageGroups(r.data.stage_groups || {});
      setStages(r.data.review_stages || []);
      setGroups(r.data.available_groups || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/contracts/admin-settings/stage-groups', { stage_groups: stageGroups });
      toast.success('Default stage groups saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <CollapsibleSection title="Default Stage Approval Groups" defaultOpen>
      <p className="text-sm text-muted-foreground mb-4">
        Assign a default approval group to each stage. Individual contracts can override these defaults.
      </p>
      <div className="space-y-3">
        {stages.map(stage => {
          const assigned = stageGroups[stage.id];
          const group = groups.find(g => g.group_code === assigned?.group_code);
          return (
            <div key={stage.id} className="flex items-center gap-4 p-3 rounded-lg border bg-card">
              <div className="w-40"><p className="text-sm font-semibold">{stage.label}</p></div>
              <select value={assigned?.group_code || ''}
                onChange={e => setStageGroups(prev => ({ ...prev, [stage.id]: e.target.value ? { group_code: e.target.value } : null }))}
                className="flex-1 h-8 rounded-lg border border-input bg-background px-2 text-sm">
                <option value="">No approval required</option>
                {groups.map(g => (
                  <option key={g.group_code} value={g.group_code}>
                    {g.group_code} — {g.description} ({g.routing_method === 'all_at_once' ? 'All Must Approve' : g.routing_method === 'sequential' ? 'Sequential' : 'Any One'}, {(g.members || []).length} members)
                  </option>
                ))}
              </select>
              {group && <Badge variant="outline" className="text-[10px] whitespace-nowrap">{(group.members || []).length} members</Badge>}
            </div>
          );
        })}
        {stages.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No review stages configured. Add stages above first.</p>}
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Default Groups'}</Button>
      </div>
    </CollapsibleSection>
  );
}
